import { Filter, onChangeFunc } from 'components/core/search-filter/filter'

import { CancelFunc, emptyCancel } from 'lib/cancel'
import { AppUserRef, AppUserServiceSearchRequest } from 'pbts/c1/api/app/v1/app_user'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { appUserApi, AppUserViewExpanded } from 'redux_toolkit/apps'

type AppUserFilterProps = {
    selected?: Array<AppUserRef>
    onChange: onChangeFunc<AppUserViewExpanded>
    appId?: string
    userId?: string
}

const renderLabel = (option: AppUserViewExpanded): string => option.appUser?.displayName || option.id || ''

const getOptionSelected = (option1: AppUserViewExpanded, option2: AppUserViewExpanded): boolean => option1.id === option2.id
    && option1.appId === option2.appId

export const AppUserFilter = ({ selected, onChange, appId, userId }: AppUserFilterProps): React.ReactElement => {
    const intl = useIntl()
    const [query, setQuery] = useState<string>('')

    const filterAllAppUsers = useMemo(() => AppUserServiceSearchRequest.create({
        query,
        ...(appId ? { appId } : {}),
        userIds: userId ? [userId] : [],
        expandMask: { paths: ['*'] },
    }), [appId, query, userId])

    const { trigger: _trigger, data, isError, isLoading } = appUserApi.useLazySearch()

    const trigger = useCallback(() => _trigger(filterAllAppUsers), [_trigger, filterAllAppUsers])

    const search = (q: string): CancelFunc => {
        setQuery(q || '')
        return emptyCancel
    }

    const allAppUsers = data ? data.flat() : []

    const initialSelected = useRef(selected)

    useEffect(() => {
        if (initialSelected.current) trigger()
    }, [trigger])

    return <Filter<AppUserViewExpanded>
        label={intl.formatMessage({
            id: 'components_core_search_filter_app_user_label',
            defaultMessage: 'Account',
        })}
        options={allAppUsers}
        selected={allAppUsers.filter((appUser) => selected?.some((s) => s.id === appUser.id && s.appId === appUser.appId))}
        onChange={onChange}
        getOptionSelected={getOptionSelected}
        renderLabel={renderLabel}
        error={isError}
        errorText={intl.formatMessage({
            id: 'components_core_search_filter_app_user_error',
            defaultMessage: 'Error loading accounts',
        })}
        loading={isLoading}
        onOpen={trigger}
        search={search}
        clearOnBlur={false}
        clearOnClick={false}
    />
}
